import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import Thumb from './Thumb';
import { Search, X, Plus, Package } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

/**
 * Modal dialog for adding items or kits to a project. Two tabs (items / kits),
 * shared search filter, availability + duplicate-detection in the list.
 *
 * Multi-quantity is supported on items: adding the same item again increments
 * the project's reservation by 1 (project.itemCodes is a multiset list).
 */
export default function AddGearDialog({ open, onClose, project }) {
  const {
    items,
    kits,
    categories,
    projectAddItem,
    projectAddKit,
    itemAvailableQty,
    categoryColor,
  } = useApp();
  const [tab, setTab] = useState('items');
  const [q, setQ] = useState('');
  // Items tab only — narrows the list by category. 'All' = no filter.
  const [selectedCategory, setSelectedCategory] = useState('All');

  if (!open) return null;

  const inKits = new Set(project.kitCodes || []);
  // Items implicitly on the project via a kit cascade — for clearer messaging
  const cascadedItems = new Set();
  (project.kitCodes || []).forEach((kc) => {
    const kit = kits.find((k) => k.code === kc);
    if (kit) (kit.itemCodes || []).forEach((ic) => cascadedItems.add(ic));
  });

  const filteredItems = items.filter((it) => {
    if (selectedCategory !== 'All' && it.category !== selectedCategory) return false;
    if (!q) return true;
    const hay = `${it.code} ${it.name} ${it.category}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });
  const filteredKits = kits.filter((k) => {
    if (!q) return true;
    const hay = `${k.code} ${k.name}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="surface rounded-lg w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-900">
          <h2 className="text-sm font-semibold text-white">Add gear to project</h2>
          <button onClick={onClose} className="text-neutral-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-neutral-900">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, code, or category…"
                className="w-full bg-neutral-950 border border-neutral-800 text-white text-sm rounded-md pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/60"
              />
            </div>
            {tab === 'items' && (
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger
                  className="w-44 bg-neutral-950 border-neutral-800 text-white text-sm focus:ring-amber-500/40 focus:ring-offset-0 focus:ring-2 hover:bg-neutral-900"
                  data-testid="add-gear-category-filter"
                >
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                  <SelectItem
                    value="All"
                    className="text-white focus:bg-amber-500/10 focus:text-amber-300"
                  >
                    All categories
                  </SelectItem>
                  {categories.map((c) => (
                    <SelectItem
                      key={c.id || c.name}
                      value={c.name}
                      className="text-white focus:bg-amber-500/10 focus:text-amber-300"
                    >
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ background: c.color }}
                        />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex items-center gap-1 mt-3">
            <button
              onClick={() => setTab('items')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                tab === 'items'
                  ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                  : 'text-neutral-400 border border-transparent hover:bg-neutral-900'
              }`}
            >
              Items ({filteredItems.length})
            </button>
            <button
              onClick={() => setTab('kits')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                tab === 'kits'
                  ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                  : 'text-neutral-400 border border-transparent hover:bg-neutral-900'
              }`}
            >
              Kits ({filteredKits.length})
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {tab === 'items' && (
            <div className="divide-y divide-neutral-900">
              {filteredItems.length === 0 && (
                <div className="p-8 text-center text-sm text-neutral-500">No matches.</div>
              )}
              {filteredItems.map((it) => {
                const avail = itemAvailableQty(it.code);
                const directQty = (project.itemCodes || []).filter((c) => c === it.code).length;
                const alreadyViaKit = directQty === 0 && cascadedItems.has(it.code);
                const isMedia = it.category === 'Media';
                const mediaBlocked = isMedia && it.mediaStatus && it.mediaStatus !== 'Ready';
                const canAdd = avail > 0 && !mediaBlocked;
                return (
                  <div key={it.code} className="flex items-center gap-3 px-4 py-2.5 row-hover">
                    <Thumb src={it.image} name={it.name} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{it.name}</div>
                      <div className="text-[11px] text-neutral-500 flex items-center gap-2">
                        <span className="font-mono">{it.code}</span>
                        <span className="cat-dot" style={{ background: categoryColor(it.category) }} />
                        <span>{it.category}</span>
                        {isMedia && it.mediaStatus && (
                          <span
                            className={`text-[9px] tracking-wider uppercase px-1.5 py-0.5 rounded border ${
                              it.mediaStatus === 'Ready'
                                ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                                : it.mediaStatus === 'In Use'
                                ? 'bg-amber-500/10 text-amber-300 border-amber-500/30'
                                : it.mediaStatus === 'Verifying'
                                ? 'bg-sky-500/10 text-sky-300 border-sky-500/30'
                                : 'bg-rose-500/10 text-rose-300 border-rose-500/30'
                            }`}
                            data-testid={`media-status-badge-${it.code}`}
                          >
                            {it.mediaStatus}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right leading-tight">
                      {directQty > 0 && (
                        <div
                          className="text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 inline-block mb-0.5"
                          data-testid={`already-on-project-${it.code}`}
                        >
                          {directQty} on project
                        </div>
                      )}
                      {alreadyViaKit && (
                        <div
                          className="text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 inline-block mb-0.5"
                          data-testid={`already-on-project-${it.code}`}
                        >
                          On project (via kit)
                        </div>
                      )}
                      <div
                        className="text-[11px] text-neutral-400"
                        data-testid={`avail-${it.code}`}
                      >
                        <span className="text-white font-medium">{avail}</span>
                        <span className="text-neutral-500"> of {it.totalQty} free</span>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await projectAddItem(project.id, it.code);
                          toast.success(`Added 1 × ${it.name} to project`);
                        } catch (e) {
                          toast.error(e.message || 'Could not add item');
                        }
                      }}
                      disabled={!canAdd}
                      title={
                        canAdd
                          ? directQty > 0
                            ? `Add another ${it.name} (total reservations on this project will be ${directQty + 1})`
                            : `Add 1 ${it.name}`
                          : mediaBlocked
                          ? `Media status is "${it.mediaStatus}" — must be Ready before checkout. Resolve on the Media Management page.`
                          : 'No units free — already fully reserved'
                      }
                      className={`text-[11px] px-2.5 py-1 rounded-md flex items-center gap-1 ${
                        !canAdd
                          ? 'border border-neutral-800 text-neutral-600 cursor-not-allowed'
                          : 'btn-amber'
                      }`}
                      data-testid={`add-item-${it.code}`}
                    >
                      <Plus size={11} /> Add{directQty > 0 ? ' another' : ''}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {tab === 'kits' && (
            <div className="divide-y divide-neutral-900">
              {filteredKits.length === 0 && (
                <div className="p-8 text-center text-sm text-neutral-500">No matches.</div>
              )}
              {filteredKits.map((k) => {
                const onProject = inKits.has(k.code);
                return (
                  <div key={k.code} className="flex items-center gap-3 px-4 py-2.5 row-hover">
                    <Thumb src={k.image} name={k.name} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate flex items-center gap-2">
                        <Package size={12} className="text-amber-400" />
                        {k.name}
                      </div>
                      <div className="text-[11px] text-neutral-500">
                        <span className="font-mono">{k.code}</span> · {k.itemCodes.length} items
                      </div>
                    </div>
                    {onProject && (
                      <span
                        className="text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30"
                        data-testid={`kit-already-on-project-${k.code}`}
                      >
                        On project
                      </span>
                    )}
                    <button
                      onClick={async () => {
                        try {
                          await projectAddKit(project.id, k.code);
                          toast.success(`${k.name} added`);
                        } catch (e) {
                          toast.error(e.message || 'Could not add kit');
                        }
                      }}
                      disabled={onProject}
                      className={`text-[11px] px-2.5 py-1 rounded-md flex items-center gap-1 ${
                        onProject
                          ? 'border border-neutral-800 text-neutral-600 cursor-not-allowed'
                          : 'btn-amber'
                      }`}
                    >
                      <Plus size={11} /> Add
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
